import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { LANDING } from "@/constants/testIds";
import TestimonialCarousel from "@/components/TestimonialCarousel";
import DiscordCTA from "@/components/DiscordCTA";
import LessonCounter from "@/components/LessonCounter";
import { useSessionStatuses, usePublicNextHighImpact } from "@/components/SessionsClock";
import { fmtDuration } from "@/lib/sessions";
import { useAuth } from "@/context/AuthContext";
import { SocialLinks } from "@/components/SocialLinks";
import {
  ArrowRight, ArrowUpRight, LineChart, Landmark, Coins, TrendingUp, TrendingDown,
  ClipboardCheck, Sparkles, Play, ShieldCheck,
  FlaskConical, MapPin, Wifi, Lock, Check, PlayCircle,
} from "lucide-react";

const PROFESSOR_PORTRAIT_IMG = "https://customer-assets.emergentagent.com/job_wealth-academy-46/artifacts/3pofni7l_IMG_9053.jpeg";
const PROFESSOR_LAB_IMG = "https://customer-assets.emergentagent.com/job_wealth-academy-46/artifacts/0dwadkwh_IMG_9047.png";
const FOUNDER_VIDEO_URL = "https://videos.pexels.com/video-files/2169880/2169880-uhd_2560_1440_30fps.mp4";
const FOUNDER_VIDEO_POSTER = "https://images.unsplash.com/photo-1505142468610-359e7d316be0?auto=format&fit=crop&w=1400&q=80";

const LANDING_TEXT = {
  institutionalBadge: "landingUi2.institutionalBadge",
  heroTitle1: "landingUi2.heroTitle1",
  heroTitle2: "landingUi2.heroTitle2",
  heroDescription: "landingUi2.heroDescription",
  heroCtaPrimary: "landingUi2.heroCtaPrimary",
  heroCtaSecondary: "landingUi2.heroCtaSecondary",
  yearsInLab: "landingUi2.yearsInLab",
  availableIn: "landingUi2.availableIn",
  availableInAria: "landingUi2.availableInAria",
  todayInLab: "landingUi2.todayInLab",
  labBriefProfessor: "landingUi2.labBriefProfessor",
  dailyBrief: "landingUi2.dailyBrief",
  setupsCount: "landingUi2.setupsCount",
  marketClock: "landingUi2.marketClock",
  nextPrint: "landingUi2.nextPrint",
  highImpact: "landingUi2.highImpact",
  cpiTitle: "landingUi2.cpiTitle",
  cpiConsensus: "landingUi2.cpiConsensus",
  realTimeIntelligence: "landingUi2.realTimeIntelligence",
  readMarketTitle: "landingUi2.readMarketTitle",
  readMarketDescription: "landingUi2.readMarketDescription",
  liveTapeReads: "landingUi2.liveTapeReads",
  correlationsDaily: "landingUi2.correlationsDaily",
  macroCalendar: "landingUi2.macroCalendar",
  indices: "landingUi2.indices",
  commodities: "landingUi2.commodities",
  forex: "landingUi2.forex",
  disciplinedJournaling: "landingUi2.disciplinedJournaling",
  everyTradeGradedTitle: "landingUi2.everyTradeGradedTitle",
  everyTradeGradedDescription: "landingUi2.everyTradeGradedDescription",
  autoPnl: "landingUi2.autoPnl",
  emotionTracking: "landingUi2.emotionTracking",
  aiWeeklyReport: "landingUi2.aiWeeklyReport",
  tradeGrader: "landingUi2.tradeGrader",
  graded: "landingUi2.graded",
  execution: "landingUi2.execution",
  bias: "landingUi2.bias",
  setup: "landingUi2.setup",
  timing: "landingUi2.timing",
  deskNote: "landingUi2.deskNote",
  deskNoteText: "landingUi2.deskNoteText",
  structuredPlaybook: "landingUi2.structuredPlaybook",
  growingLibraryTitle: "landingUi2.growingLibraryTitle",
  growingLibraryDescription: "landingUi2.growingLibraryDescription",
  exploreCurriculum: "landingUi2.exploreCurriculum",
  chapterTitle: "landingUi2.chapterTitle",
  lessonOneMath: "landingUi2.lessonOneMath",
  lessonSizing: "landingUi2.lessonSizing",
  lessonDrills: "landingUi2.lessonDrills",
  insideMembersArea: "landingUi2.insideMembersArea",
  whatsWaitingTitle: "landingUi2.whatsWaitingTitle",
  whatsWaitingDescription: "landingUi2.whatsWaitingDescription",
  dashboard: "landingUi2.dashboard",
  continueWhereLeft: "landingUi2.continueWhereLeft",
  chapterPositionSizing: "landingUi2.chapterPositionSizing",
  chapterSupportResistance: "landingUi2.chapterSupportResistance",
  chapterMacro: "landingUi2.chapterMacro",
  wkStreak: "landingUi2.wkStreak",
  rrAvg: "landingUi2.rrAvg",
  grade: "landingUi2.grade",
  tradeJournal: "landingUi2.tradeJournal",
  everyTradeFeeling: "landingUi2.everyTradeFeeling",
  emotionTrackingPhases: "landingUi2.emotionTrackingPhases",
  theProfessor: "landingUi2.theProfessor",
  alwaysInCorner: "landingUi2.alwaysInCorner",
  chatQuestion: "landingUi2.chatQuestion",
  chatAnswer: "landingUi2.chatAnswer",
  chatFollowup: "landingUi2.chatFollowup",
  certificates: "landingUi2.certificates",
  proofReps: "landingUi2.proofReps",
  certFoundations: "landingUi2.certFoundations",
  certPositionSizing: "landingUi2.certPositionSizing",
  certAscent: "landingUi2.certAscent",
  certPeakGraduate: "landingUi2.certPeakGraduate",
  certBasic: "landingUi2.certBasic",
  certMastery: "landingUi2.certMastery",
  certDiploma: "landingUi2.certDiploma",
  locked: "landingUi2.locked",
  certDescription: "landingUi2.certDescription",
  residencySelective: "landingUi2.residencySelective",
  residencyTitle: "landingUi2.residencyTitle",
  residencyDescription: "landingUi2.residencyDescription",
  applyNow: "landingUi2.applyNow",
  membersOnlyUnlock: "landingUi2.membersOnlyUnlock",
  benchSide: "landingUi2.benchSide",
  benchSideDesc: "landingUi2.benchSideDesc",
  remoteLab: "landingUi2.remoteLab",
  remoteLabDesc: "landingUi2.remoteLabDesc",
  soloBench: "landingUi2.soloBench",
  soloBenchDesc: "landingUi2.soloBenchDesc",
  professorBadge: "landingUi2.professorBadge",
  professorTitle: "landingUi2.professorTitle",
  professorDescription: "landingUi2.professorDescription",
  professorName: "landingUi2.professorName",
  professorRole: "landingUi2.professorRole",
  ready: "landingUi2.ready",
  finalCtaTitle: "landingUi2.finalCtaTitle",
  finalCtaDescription: "landingUi2.finalCtaDescription",
  getStartedToday: "landingUi2.getStartedToday",
  takeSixtySecondQuiz: "landingUi2.takeSixtySecondQuiz",
  connectDiscord: "landingUi2.connectDiscord",
  followLab: "landingUi2.followLab",
  followLabTitle: "landingUi2.followLabTitle",
  joinDiscordCommunity: "landingUi2.joinDiscordCommunity",
  instagram: "landingUi2.instagram",
  twitter: "landingUi2.twitter",
  youtube: "landingUi2.youtube",
};

// Live-feel ticker + watchlist data. Static values, but the pill styling
// mimics Columis's real-time-quote look. Kept small so a mid-week refresh
// stays trivial.
const TICKERS = [
  { symbol: "NAS100", price: "21,842.5", pct: "+0.84", up: true },
  { symbol: "US30",   price: "44,127.0", pct: "+0.42", up: true },
  { symbol: "XAUUSD", price: "2,655.3",  pct: "+1.20", up: true },
  { symbol: "EURUSD", price: "1.0426",   pct: "-0.15", up: false },
  { symbol: "GBPJPY", price: "196.84",   pct: "+0.31", up: true },
  { symbol: "USOIL",  price: "72.18",    pct: "-0.52", up: false },
];

const WATCHLIST = [
  { icon: LineChart, name: "NAS100 · Nasdaq 100",  price: "21,842.5", pct: "+0.84", up: true },
  { icon: Landmark, name: "US30 · Dow Jones",      price: "44,127.0", pct: "+0.42", up: true },
  { icon: Coins,   name: "XAUUSD · Spot Gold",     price: "2,655.3",  pct: "+1.20", up: true },
  { icon: LineChart, name: "EURUSD · Euro/Dollar", price: "1.0426",   pct: "-0.15", up: false },
];

export default function Landing() {
  const { t } = useTranslation();
  const live = useSessionStatuses();
  const nextPrint = usePublicNextHighImpact();
  const { user } = useAuth();
  const isMember = !!user;
  const residencyHref = (program) => isMember ? `/residency/apply?program=${program}` : "/pricing?highlight=residency";
  const residencyDetailsHref = isMember ? "/residency" : "/pricing?highlight=residency";

  return (
    <div className="bg-white text-slate-900">
      {/* ─── HERO ────────────────────────────────────────────────────── */}
      <section
        data-testid={LANDING.hero}
        className="relative overflow-hidden bg-[radial-gradient(ellipse_at_top,rgba(37,99,235,0.10),transparent_65%),radial-gradient(ellipse_at_bottom_right,rgba(245,158,11,0.06),transparent_60%)]"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 pt-16 pb-24 lg:pt-24 lg:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-blue-200/70 bg-blue-50/60 px-3 py-1.5 text-[10px] uppercase tracking-[0.28em] text-blue-800 font-bold">
              <Sparkles size={11} /> {t(LANDING_TEXT.institutionalBadge)}
            </div>
            <h1 className="mt-6 font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tighter text-slate-900">
              {t(LANDING_TEXT.heroTitle1)}<br />
              <span className="bg-gradient-to-r from-blue-900 via-blue-700 to-blue-600 bg-clip-text text-transparent">{t(LANDING_TEXT.heroTitle2)}</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
              {t(LANDING_TEXT.heroDescription)}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/onboarding">
                <Button data-testid={LANDING.heroCtaPrimary} size="lg" className="bg-blue-800 hover:bg-blue-700 text-white h-12 px-7 rounded-full font-semibold shadow-[0_8px_24px_-8px_rgba(30,64,175,0.6)]">
                  {t(LANDING_TEXT.heroCtaPrimary)} <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button data-testid={LANDING.heroCtaSecondary} size="lg" variant="outline" className="h-12 px-7 rounded-full border-2 border-slate-200 hover:border-blue-800 text-slate-900 font-semibold bg-white">
                  {t(LANDING_TEXT.heroCtaSecondary)}
                </Button>
              </Link>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
              <LessonCounter />
              <div>
                <div className="font-display font-black text-3xl sm:text-4xl text-slate-900 tracking-tighter">10+</div>
                <div className="mt-1 text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t(LANDING_TEXT.yearsInLab)}</div>
              </div>
              <div data-testid="landing-languages">
                <div className="flex items-center gap-1.5 h-[38px] sm:h-[44px]" aria-label={t(LANDING_TEXT.availableInAria)}>
                  {[
                    { flag: "🇺🇸", code: "EN" },
                    { flag: "🇪🇸", code: "ES" },
                    { flag: "🇫🇷", code: "FR" },
                  ].map((l) => (
                    <span
                      key={l.code}
                      title={l.code}
                      className="text-2xl sm:text-[26px] leading-none drop-shadow-sm"
                    >
                      {l.flag}
                    </span>
                  ))}
                </div>
                <div className="mt-1 text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t(LANDING_TEXT.availableIn)}</div>
              </div>
            </div>
          </div>

          {/* Floating widget stack */}
          <div className="lg:col-span-5 relative">
            <div className="absolute -inset-8 bg-[radial-gradient(circle_at_50%_50%,rgba(37,99,235,0.15),transparent_70%)] pointer-events-none" />
            <div className="relative space-y-4">

              {/* WIDGET 1 · Today in the lab — dated tape-read briefing */}
              <div className="rounded-2xl bg-white border border-slate-200 shadow-[0_20px_50px_-20px_rgba(15,23,42,0.15)] p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-slate-500">
                    <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" /></span>
                    {t(LANDING_TEXT.todayInLab)}
                  </div>
                  <div className="text-[10px] text-slate-400 font-mono">{live.now.toISOString().slice(11, 16)} UTC</div>
                </div>
                <div className="space-y-2.5">
                  {[
                    { pair: "NAS100", note: t("landingUi2.tapeReadNas100"), tone: "up" },
                    { pair: "XAUUSD", note: t("landingUi2.tapeReadXauusd"), tone: "up" },
                    { pair: "DXY",    note: t("landingUi2.tapeReadDxy"), tone: "down" },
                  ].map((n) => (
                    <div key={n.pair} className="flex gap-3">
                      <div className={`shrink-0 mt-1 w-1.5 h-1.5 rounded-full ${n.tone === "up" ? "bg-emerald-500" : "bg-rose-500"}`} />
                      <div className="min-w-0">
                        <div className="text-[11px] uppercase tracking-widest font-bold text-slate-900">{n.pair}</div>
                        <div className="text-[12px] text-slate-600 leading-snug">{n.note}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                  <div className="text-[10px] text-slate-500">
                    <span className="font-semibold text-slate-700">{t(LANDING_TEXT.labBriefProfessor)}</span> · {t(LANDING_TEXT.dailyBrief)}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-blue-800">{t(LANDING_TEXT.setupsCount, { count: 3 })}</div>
                </div>
                {/* Compact session strip — a Professor knows what's open */}
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2 text-[10px]">
                  {live.rows.map((r, i) => ({ name: ["SYD", "TYO", "LDN", "NY"][i], status: r.status })).map((s) => (
                    <div key={s.name} className="flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${
                        s.status === "open" ? "bg-emerald-500 animate-pulse" :
                        s.status === "next" ? "bg-amber-500" : "bg-slate-300"
                      }`} />
                      <span className={`font-mono font-bold tracking-wider ${
                        s.status === "open" ? "text-emerald-700" :
                        s.status === "next" ? "text-amber-600" : "text-slate-400"
                      }`}>{s.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* WIDGET 2 · Market Clock · sessions + next macro print */}
              <div className="rounded-2xl bg-slate-950 text-white shadow-[0_20px_50px_-20px_rgba(15,23,42,0.35)] p-5 relative overflow-hidden">
                <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-amber-500/20 blur-3xl pointer-events-none" />
                <div className="absolute -bottom-16 -left-16 w-40 h-40 rounded-full bg-blue-600/25 blur-3xl pointer-events-none" />
                <div className="relative">
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">{t(LANDING_TEXT.marketClock)}</div>
                    <div data-testid="landing-clock-utc" className="text-[10px] text-slate-400 font-mono">{live.now.toISOString().slice(11, 16)} UTC</div>
                  </div>
                  {/* Four sessions with open/closed dots */}
                  <div className="grid grid-cols-4 gap-2 mb-5">
                    {live.rows.map((r) => ({
                      name: t(`sessions.${r.id}`),
                      status: r.status,
                      detail: r.open ? t("sessions.leftShort", { d: fmtDuration(r.mins) }) : t("sessions.opensIn", { d: fmtDuration(r.mins) }),
                    })).map((s) => (
                      <div key={s.name} className="text-center">
                        <div className="relative inline-flex items-center justify-center mb-1.5">
                          {s.status === "open" && <span className="absolute w-4 h-4 rounded-full bg-emerald-400/40 animate-ping" />}
                          <span className={`relative w-2.5 h-2.5 rounded-full ${
                            s.status === "open" ? "bg-emerald-400" :
                            s.status === "next" ? "bg-amber-400" : "bg-slate-600"
                          }`} />
                        </div>
                        <div className={`text-[10px] font-display font-bold ${
                          s.status === "open" ? "text-emerald-300" :
                          s.status === "next" ? "text-amber-300" : "text-slate-500"
                        }`}>{s.name}</div>
                        <div className="text-[9px] text-slate-500 mt-0.5 leading-tight">{s.detail}</div>
                      </div>
                    ))}
                  </div>
                  {/* Next macro event */}
                  <div className="rounded-lg bg-white/5 border border-white/10 backdrop-blur px-3 py-2.5">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">{t(LANDING_TEXT.nextPrint)}</div>
                      <div className="text-[9px] uppercase tracking-widest font-bold text-amber-300">{t(LANDING_TEXT.highImpact)}</div>
                    </div>
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="font-display font-bold text-lg text-white leading-tight">{nextPrint ? `${nextPrint.flag} ${nextPrint.event}` : t("landingUi2.weekClear")}</div>
                      <div data-testid="landing-next-print-countdown" className="font-mono font-black text-xl text-amber-300 tabular-nums">{nextPrint ? fmtDuration(Math.max(1, Math.round((new Date(nextPrint.datetime_utc) - live.now) / 60000))) : "—"}</div>
                    </div>
                    <div className="mt-1 text-[10px] text-slate-400 leading-snug">
                      {nextPrint ? t("landingUi2.liveConsensus", { f: nextPrint.forecast ?? "—", p: nextPrint.previous ?? "—" }) : t("landingUi2.noPrints")}
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ─── MARQUEE TICKER ──────────────────────────────────────────── */}
      <section data-testid="landing-ticker-marquee" className="border-y border-slate-200 bg-white overflow-hidden">
        <div className="flex gap-10 py-4 whitespace-nowrap animate-[marquee_38s_linear_infinite]" style={{ animationName: "marquee" }}>
          {[...TICKERS, ...TICKERS, ...TICKERS].map((tk, i) => (
            <div key={i} className="inline-flex items-center gap-3 shrink-0">
              <span className="text-[11px] uppercase tracking-widest font-bold text-slate-500">{tk.symbol}</span>
              <span className="font-mono text-sm font-semibold text-slate-900">{tk.price}</span>
              <span className={`inline-flex items-center gap-0.5 text-[11px] font-bold px-2 py-0.5 rounded-full ${tk.up ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                {tk.up ? <TrendingUp size={10} /> : <TrendingDown size={10} />} {tk.pct}%
              </span>
            </div>
          ))}
        </div>
        <style>{`@keyframes marquee { from { transform: translateX(0);} to { transform: translateX(-33.333%);} }`}</style>
      </section>

      {/* ─── FEATURE SHOWCASE (alternating rows) ─────────────────────── */}
      <section data-testid={LANDING.featuresSection} className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-24 space-y-24">
        {/* Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-blue-700 font-bold mb-4">{t(LANDING_TEXT.realTimeIntelligence)}</div>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl text-slate-900 tracking-tighter leading-tight">{t(LANDING_TEXT.readMarketTitle)}</h2>
            <p className="mt-5 text-slate-600 leading-relaxed text-lg">
              {t(LANDING_TEXT.readMarketDescription)}
            </p>
            <ul className="mt-6 space-y-2 text-sm text-slate-700">
              {[t(LANDING_TEXT.liveTapeReads), t(LANDING_TEXT.correlationsDaily), t(LANDING_TEXT.macroCalendar)].map((f) => (
                <li key={f} className="flex items-start gap-2"><Check size={16} className="text-blue-800 shrink-0 mt-0.5" /> {f}</li>
              ))}
            </ul>
          </div>
          <div data-testid={LANDING.featureCard} className="rounded-3xl bg-white border border-slate-200/80 p-6 shadow-[0_20px_50px_-20px_rgba(15,23,42,0.12)]">
            <div className="flex gap-2 mb-4">
              {[t(LANDING_TEXT.indices), t(LANDING_TEXT.commodities), t(LANDING_TEXT.forex)].map((tab, i) => (
                <div key={tab} className={`text-[11px] font-semibold px-3 py-1.5 rounded-full ${i === 0 ? "bg-blue-800 text-white" : "bg-slate-100 text-slate-600"}`}>{tab}</div>
              ))}
            </div>
            <div className="space-y-2.5">
              {WATCHLIST.map((w) => {
                const Icon = w.icon;
                return (
                  <div key={w.name} className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${w.up ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}><Icon size={14} /></div>
                      <div className="min-w-0 text-sm font-semibold text-slate-900 truncate">{w.name}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="font-mono text-sm font-semibold text-slate-900">{w.price}</div>
                      <div className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${w.up ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{w.pct}%</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Row 2 — reversed */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div data-testid={LANDING.featureCard} className="order-2 lg:order-1 rounded-3xl bg-white border border-slate-200/80 p-8 shadow-[0_20px_50px_-20px_rgba(15,23,42,0.12)]">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{t(LANDING_TEXT.tradeGrader)}</div>
              <div className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full"><Check size={11} /> {t(LANDING_TEXT.graded)}</div>
            </div>
            <div className="flex items-baseline gap-4">
              <div className="font-display font-black text-7xl text-slate-900 tracking-tighter">94</div>
              <div className="text-sm text-slate-500">/ 100 {t(LANDING_TEXT.execution)}</div>
            </div>
            <div className="mt-4 grid grid-cols-4 gap-2 text-[10px]">
              {[[t(LANDING_TEXT.bias), "A"], [t(LANDING_TEXT.setup), "A"], ["R:R", "3.2"], [t(LANDING_TEXT.timing), "A-"]].map(([k, v]) => (
                <div key={k} className="rounded-lg bg-slate-50 border border-slate-200/70 px-2.5 py-2">
                  <div className="text-slate-500 uppercase tracking-widest">{k}</div>
                  <div className="mt-0.5 font-mono font-bold text-slate-900">{v}</div>
                </div>
              ))}
            </div>
            <div className="mt-5 rounded-lg bg-blue-50/60 border border-blue-100 px-3 py-2 text-[12px] text-slate-700 leading-relaxed">
              <span className="font-semibold text-blue-900">{t(LANDING_TEXT.deskNote)}</span> {t(LANDING_TEXT.deskNoteText)}
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-blue-700 font-bold mb-4">{t(LANDING_TEXT.disciplinedJournaling)}</div>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl text-slate-900 tracking-tighter leading-tight">{t(LANDING_TEXT.everyTradeGradedTitle)}</h2>
            <p className="mt-5 text-slate-600 leading-relaxed text-lg">
              {t(LANDING_TEXT.everyTradeGradedDescription)}
            </p>
            <ul className="mt-6 space-y-2 text-sm text-slate-700">
              {[t(LANDING_TEXT.autoPnl), t(LANDING_TEXT.emotionTracking), t(LANDING_TEXT.aiWeeklyReport)].map((f) => (
                <li key={f} className="flex items-start gap-2"><Check size={16} className="text-blue-800 shrink-0 mt-0.5" /> {f}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Row 3 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-blue-700 font-bold mb-4">{t(LANDING_TEXT.structuredPlaybook)}</div>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl text-slate-900 tracking-tighter leading-tight">{t(LANDING_TEXT.growingLibraryTitle)}</h2>
            <p className="mt-5 text-slate-600 leading-relaxed text-lg">
              {t(LANDING_TEXT.growingLibraryDescription)}
            </p>
            <Link to="/pricing" className="inline-flex items-center gap-1.5 mt-6 text-blue-800 font-semibold hover:text-blue-700">
              {t(LANDING_TEXT.exploreCurriculum)} <ArrowUpRight size={16} />
            </Link>
          </div>
          <div data-testid={LANDING.featureCard} className="rounded-3xl bg-white border border-slate-200/80 p-6 shadow-[0_20px_50px_-20px_rgba(15,23,42,0.12)]">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-4">{t(LANDING_TEXT.chapterTitle)}</div>
            <div className="rounded-xl bg-slate-950 aspect-video mb-4 relative overflow-hidden flex items-center justify-center">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(37,99,235,0.35),transparent_60%)]" />
              <div className="relative w-14 h-14 rounded-full bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center">
                <Play size={22} className="text-white ml-1" fill="white" />
              </div>
            </div>
            <div className="space-y-2">
              {[t(LANDING_TEXT.lessonOneMath), t(LANDING_TEXT.lessonSizing), t(LANDING_TEXT.lessonDrills)].map((l, i) => (
                <div key={l} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2.5">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${i === 0 ? "bg-blue-800 text-white" : "bg-slate-100 text-slate-500"}`}>{i + 1}</div>
                    <div className={i === 0 ? "font-semibold text-slate-900" : "text-slate-600"}>{l}</div>
                  </div>
                  <div className="text-[11px] text-slate-400 font-mono">08:{20 + i * 15}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── INSIDE THE MEMBERS AREA ─────────────────────────────────── */}
      <section data-testid={LANDING.partnersSection} className="bg-slate-50 border-y border-slate-200/70">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-24">
          <div className="max-w-2xl mb-12">
            <div className="text-xs uppercase tracking-[0.28em] text-blue-700 font-bold mb-3">{t(LANDING_TEXT.insideMembersArea)}</div>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl text-slate-900 tracking-tighter leading-tight">
              {t(LANDING_TEXT.whatsWaitingTitle)}
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed text-lg">
              {t(LANDING_TEXT.whatsWaitingDescription)}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* CARD 1 · Dashboard */}
            <div data-testid={LANDING.partnerLogo} className="rounded-2xl bg-white border border-slate-200/80 p-5 hover:-translate-y-1 hover:border-blue-300 hover:shadow-[0_20px_40px_-20px_rgba(30,64,175,0.25)] transition-all duration-300">
              <div className="text-[10px] uppercase tracking-widest font-bold text-blue-700">{t(LANDING_TEXT.dashboard)}</div>
              <div className="mt-2 font-display font-bold text-lg text-slate-900 leading-tight">{t(LANDING_TEXT.continueWhereLeft)}</div>
              <div className="mt-4 space-y-1.5">
                {[t(LANDING_TEXT.chapterPositionSizing), t(LANDING_TEXT.chapterSupportResistance), t(LANDING_TEXT.chapterMacro)].map((c, i) => (
                  <div key={c} className={`flex items-center justify-between rounded-md px-2.5 py-2 text-[11px] ${i === 0 ? "bg-blue-50 border border-blue-100" : "bg-slate-50"}`}>
                    <span className={`truncate ${i === 0 ? "font-semibold text-blue-900" : "text-slate-600"}`}>{c}</span>
                    <span className="text-slate-400 font-mono ml-2">{i === 0 ? "62%" : i === 1 ? "0%" : "🔒"}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-slate-100 grid grid-cols-3 gap-2 text-center">
                {[[t(LANDING_TEXT.wkStreak), "5"], [t(LANDING_TEXT.rrAvg), "2.1"], [t(LANDING_TEXT.grade), "A-"]].map(([k, v]) => (
                  <div key={k}><div className="font-mono font-bold text-slate-900 text-sm">{v}</div><div className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold">{k}</div></div>
                ))}
              </div>
            </div>

            {/* CARD 2 · Trade Journal with emotion badges */}
            <div data-testid={LANDING.partnerLogo} className="rounded-2xl bg-white border border-slate-200/80 p-5 hover:-translate-y-1 hover:border-blue-300 hover:shadow-[0_20px_40px_-20px_rgba(30,64,175,0.25)] transition-all duration-300">
              <div className="text-[10px] uppercase tracking-widest font-bold text-blue-700">{t(LANDING_TEXT.tradeJournal)}</div>
              <div className="mt-2 font-display font-bold text-lg text-slate-900 leading-tight">{t(LANDING_TEXT.everyTradeFeeling)}</div>
              <div className="mt-4 space-y-2">
                {[
                  { inst: "NAS100", side: "SHORT", pnl: "+$412",  emotions: ["💪", "😎", "🙂\u200d↕\ufe0f"] },
                  { inst: "XAUUSD", side: "LONG",  pnl: "+$186",  emotions: ["🧠", "😌", "😌"] },
                  { inst: "EURUSD", side: "SHORT", pnl: "-$74",   emotions: ["😬", "😤", "😔"] },
                ].map((r) => (
                  <div key={r.inst} className="flex items-center justify-between text-[11px] bg-slate-50 rounded-md px-2.5 py-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold text-slate-900">{r.inst}</span>
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${r.side === "LONG" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{r.side}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-0.5 text-sm leading-none">{r.emotions.map((e, i) => <span key={i}>{e}</span>)}</span>
                      <span className={`font-mono font-bold text-xs ${r.pnl.startsWith("+") ? "text-emerald-700" : "text-rose-700"}`}>{r.pnl}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-[10px] text-slate-500 leading-relaxed">{t(LANDING_TEXT.emotionTrackingPhases)}</div>
            </div>

            {/* CARD 3 · The Professor · casual chat */}
            <div data-testid={LANDING.partnerLogo} className="rounded-2xl bg-slate-950 text-white p-5 hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(15,23,42,0.35)] transition-all duration-300 relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-blue-600/25 blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="text-[10px] uppercase tracking-widest font-bold text-amber-300">{t(LANDING_TEXT.theProfessor)}</div>
                <div className="mt-2 font-display font-bold text-lg text-white leading-tight">{t(LANDING_TEXT.alwaysInCorner)}</div>
                <div className="mt-4 space-y-2">
                  <div className="ml-6 bg-blue-800/40 backdrop-blur border border-blue-500/20 rounded-lg rounded-tr-sm px-3 py-2 text-[11px] leading-snug">
                    {t(LANDING_TEXT.chatQuestion)}
                  </div>
                  <div className="mr-6 bg-white/10 backdrop-blur border border-white/10 rounded-lg rounded-tl-sm px-3 py-2 text-[11px] leading-snug text-slate-100">
                    {t(LANDING_TEXT.chatAnswer)}
                  </div>
                  <div className="ml-6 bg-blue-800/40 backdrop-blur border border-blue-500/20 rounded-lg rounded-tr-sm px-3 py-2 text-[11px] leading-snug">
                    {t(LANDING_TEXT.chatFollowup)}
                  </div>
                </div>
              </div>
            </div>

            {/* CARD 4 · Certificate wall */}
            <div data-testid={LANDING.partnerLogo} className="rounded-2xl bg-white border border-slate-200/80 p-5 hover:-translate-y-1 hover:border-blue-300 hover:shadow-[0_20px_40px_-20px_rgba(30,64,175,0.25)] transition-all duration-300">
              <div className="text-[10px] uppercase tracking-widest font-bold text-blue-700">{t(LANDING_TEXT.certificates)}</div>
              <div className="mt-2 font-display font-bold text-lg text-slate-900 leading-tight">{t(LANDING_TEXT.proofReps)}</div>
              <div className="mt-4 space-y-2">
                {[
                  { nameKey: LANDING_TEXT.certFoundations, tone: "blue",  certKey: LANDING_TEXT.certBasic,   done: true },
                  { nameKey: LANDING_TEXT.certPositionSizing, tone: "blue", certKey: LANDING_TEXT.certBasic, done: true },
                  { nameKey: LANDING_TEXT.certAscent,  tone: "amber", certKey: LANDING_TEXT.certMastery, done: true },
                  { nameKey: LANDING_TEXT.certPeakGraduate,  tone: "amber", certKey: LANDING_TEXT.certDiploma, done: false },
                ].map((c) => (
                  <div key={c.nameKey} className={`flex items-center justify-between rounded-md px-2.5 py-2 text-[11px] ${c.done ? "bg-slate-50" : "bg-slate-100/60 opacity-60"}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`inline-block w-2 h-2 rounded-full ${c.tone === "amber" ? "bg-amber-500" : "bg-blue-800"}`} />
                      <span className="truncate font-semibold text-slate-900">{t(c.nameKey)}</span>
                    </div>
                    <span className={`text-[9px] uppercase tracking-widest font-bold ${c.tone === "amber" ? "text-amber-600" : "text-blue-800"}`}>{c.done ? t(c.certKey) : t(LANDING_TEXT.locked)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-[10px] text-slate-500 leading-relaxed">{t(LANDING_TEXT.certDescription)}</div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIAL CAROUSEL (preserved) ────────────────────────── */}
      <TestimonialCarousel />

      {/* ─── RESIDENCY SPOTLIGHT ─────────────────────────────────────── */}
      <section data-testid="landing-residency-section" className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-24">
        <div className="rounded-3xl bg-slate-950 text-white p-10 sm:p-14 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-blue-600/25 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-amber-500/15 blur-3xl pointer-events-none" />
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1.5 text-[10px] uppercase tracking-[0.28em] font-bold text-amber-300">
                <FlaskConical size={11} /> {t(LANDING_TEXT.residencySelective)}
              </div>
              <h2 className="mt-6 font-display font-black text-4xl sm:text-5xl leading-[1.05] tracking-tighter">{t(LANDING_TEXT.residencyTitle)}</h2>
              <p className="mt-5 text-slate-300 leading-relaxed max-w-lg">
                {t(LANDING_TEXT.residencyDescription)}
              </p>
              <Link to={residencyDetailsHref} className="inline-flex items-center gap-1.5 mt-6 text-amber-300 hover:text-amber-200 font-semibold text-sm" data-testid="landing-residency-details">
                {isMember ? <>{t(LANDING_TEXT.applyNow)} <ArrowUpRight size={14} /></> : <><Lock size={12} /> {t(LANDING_TEXT.membersOnlyUnlock)}</>}
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { icon: MapPin, nameKey: LANDING_TEXT.benchSide, descKey: LANDING_TEXT.benchSideDesc, testid: "landing-residency-bench-side" },
                { icon: Wifi, nameKey: LANDING_TEXT.remoteLab, descKey: LANDING_TEXT.remoteLabDesc, testid: "landing-residency-remote-lab" },
                { icon: FlaskConical, nameKey: LANDING_TEXT.soloBench, descKey: LANDING_TEXT.soloBenchDesc, testid: "landing-residency-solo-bench" },
              ].map((f) => (
                <Link key={f.nameKey} to={residencyHref(t(f.nameKey).toLowerCase().replace(/ /g, "-"))} data-testid={f.testid} className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur p-5 hover:bg-white/10 transition-colors">
                  <f.icon size={20} className="text-amber-300" />
                  <div className="mt-3 font-display font-bold text-lg">{t(f.nameKey)}</div>
                  <div className="mt-1 text-xs text-slate-400">{t(f.descKey)}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── PROFESSOR ───────────────────────────────────────────────── */}
      <section data-testid="landing-professor-section" className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-blue-700 font-bold mb-4">{t(LANDING_TEXT.professorBadge)}</div>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl text-slate-900 tracking-tighter leading-tight">
              {t(LANDING_TEXT.professorTitle)}
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed text-lg">
              {t(LANDING_TEXT.professorDescription)}
            </p>
            <div className="mt-6 flex items-center gap-4">
              <img src={PROFESSOR_PORTRAIT_IMG} alt={t("landingUi2.professorImageAlt")} className="w-14 h-14 rounded-full object-cover ring-2 ring-blue-800/20" />
              <div>
                <div className="font-display font-bold text-slate-900">{t(LANDING_TEXT.professorName)}</div>
                <div className="text-xs text-slate-500">{t(LANDING_TEXT.professorRole)}</div>
              </div>
            </div>
          </div>
          <div className="order-1 lg:order-2 relative rounded-3xl overflow-hidden bg-slate-950 shadow-[0_20px_50px_-20px_rgba(15,23,42,0.35)]">
            <video
              data-testid="landing-founder-video"
              src={FOUNDER_VIDEO_URL}
              poster={FOUNDER_VIDEO_POSTER}
              className="w-full aspect-video object-cover"
              controls muted loop playsInline
            />
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ───────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-24">
        <div className="rounded-3xl bg-slate-950 p-12 sm:p-16 text-white relative overflow-hidden">
          <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-blue-600/25 blur-3xl" />
          <div className="absolute -left-24 -bottom-24 w-96 h-96 rounded-full bg-amber-500/15 blur-3xl" />
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-amber-400 font-bold mb-4">{t(LANDING_TEXT.ready)}</div>
              <h2 className="font-display font-black text-4xl sm:text-5xl leading-[1.05] tracking-tighter">{t(LANDING_TEXT.finalCtaTitle)}</h2>
              <p className="mt-4 text-slate-300 max-w-md">{t(LANDING_TEXT.finalCtaDescription)}</p>
            </div>
            <div className="flex flex-wrap gap-4 lg:justify-end">
              <Link to="/pricing">
                <Button data-testid={LANDING.pricingCta} size="lg" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold h-12 px-8 rounded-full">{t(LANDING_TEXT.getStartedToday)} <ArrowRight size={16} className="ml-2" /></Button>
              </Link>
              <Link to="/onboarding">
                <Button size="lg" variant="outline" className="border-white/20 hover:border-white text-white hover:bg-white/10 h-12 px-8 rounded-full bg-transparent">{t(LANDING_TEXT.takeSixtySecondQuiz)}</Button>
              </Link>
              <DiscordCTA variant="outline" size="lg" label={t(LANDING_TEXT.connectDiscord)} testId="landing-final-discord-cta" />
            </div>
          </div>
        </div>
      </section>

      {/* ─── SOCIAL ──────────────────────────────────────────────────── */}
      <section className="border-t border-slate-200/70 bg-white py-16">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-16 text-center">
          <div className="text-xs uppercase tracking-[0.28em] text-blue-700 font-bold mb-3">{t(LANDING_TEXT.followLab)}</div>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-slate-900 tracking-tighter">{t(LANDING_TEXT.followLabTitle)}</h2>
          <div className="mt-6 flex items-center justify-center">
            <DiscordCTA size="lg" label={t(LANDING_TEXT.joinDiscordCommunity)} testId="landing-discord-cta" signupFirst />
          </div>
          <SocialLinks testIdPrefix={LANDING.socialLink} className="mt-6 justify-center" />
        </div>
      </section>
    </div>
  );
}
